/*
 *    lesson library utilities
 */

import { UserAgent } from '@mirinae/js-utils';
import { v4 as uuidv4 } from 'uuid';
import httpAPI from '../apis/http';
import { apiPaths, apiServer } from '@mirinae/shared/defines/paths';
import { deflateNode, exporNodeStruct } from '../../editor/modules/libs/lessonLib';

const { browser } = UserAgent;

// compute ancestor context for given node
export const ancestorsFor = (node) => {
    const ancestors = {};
    let parent = node.parent;
    while (parent && parent.nodeID !== 'root') {
        ancestors[parent.nodeClass] = parent;
        parent = parent.parent;
    }
    return ancestors;
};

// post a user action event, element optional
export const postLessonEvent = async (lessonObject, event) => {
    await httpAPI('', `${apiPaths.postEvent}/${lessonObject._id}`, { data: event });
};

const updateNodesInDB = async (nodes, user) => {
    const editor = user ? { name: user.name, _id: user._id } : {};
    const data = { data: { updates: [{ nodes: nodes.map(node => deflateNode(node)) }], user: editor } };
    await httpAPI(apiServer, apiPaths.updateNodeMapByGuid, data);
};

const updateNodeInDB = async (node, user) => updateNodesInDB([node], user);

export const loadAndUpdateLessonNodeField = async (lesson, field, value, user) => {
    const { data } = await httpAPI(apiServer, apiPaths.lesson, { data: { lessonID: lesson._id } });
    if (data && data.lesson) {
        if (!data.editLock) {
            const node = data.lesson.nodeMap[data.lesson.lessonNode.nodeID];
            if (node) {
                node[field] = value;
                await updateNodeInDB(node, user);
                return true;
            }
        }
    }
};

export const refreshNodesInNodeMap = (nodeMap, options) => {
    const guid = uuidv4();
    if (options && options.root) {
        const rootNode = nodeMap[options.root];
        if (rootNode.nodeClass === 'lesson') {
            rootNode.lessonCopied = rootNode.lessonObjectID; // record source lesson so we can pick up its lesson-spec on save
            if (options.lessonUpdates) {
                Object.assign(rootNode, options.lessonUpdates);
            }
            if (options.lessonTitlePrefix) {
                rootNode.title = options.lessonTitlePrefix + rootNode.title;
            }
        }
    }
    const nodesRefreshed = Object.values(nodeMap).map(node => {
        node.lessonGuid = guid;
        ['lessonObjectID', 'backupNodeID', 'figmaNodeIDToClone', 'patternID', 'figmaFileKey', 'figmaNodeID',
            'publishDate', 'lessonFigmaNodeID', 'JIRATicket'].forEach(f => delete node[f]);
        return node;
    });
    return nodesRefreshed;
};

export const loadLessonObject = async (lessonID) => {
    const { data = {} } = await httpAPI(apiServer, apiPaths.lesson, { data: { lessonID } });
    return data;
};

export const loadAndBackupLesson = async (lesson, user, options) => {
    const { data = {} } = await httpAPI(apiServer, apiPaths.lesson, { data: { lessonID: lesson._id } });
    const { lesson: lessonObject } = data;
    if (lessonObject) {
        const backupDate = new Date().toJSON().slice(0, -5);
        let lessonSpec = null;
        try {
            const response = await httpAPI('', `${apiPaths.getLessonSpec}/${lesson._id}`, { method: 'GET' });
            lessonSpec = response.data.lessonSpec;
        } catch (e) {
            // ignore missing less-spec
        }
        const nodeStruct = { root: lessonObject.lessonNode.nodeID, timestamp: Date.now(), lessonSpec, nodeMap: lessonObject.nodeMap };
        const data = {
            data: {
                label: `${lessonObject.title}.backup.${backupDate}`,
                backupDate,
                editor: user ? user._id : null,
                title: `${lessonObject.title} ${(options && options.note) || ''}`,
                nodeStruct,
            },
        };
        await httpAPI(apiServer, apiPaths.backup, data); // could be async??
    }
};

export const loadAndExportLesson = async (lesson, user) => {
    const { data = {} } = await httpAPI(apiServer, apiPaths.lesson, { data: { lessonID: lesson._id } });
    const { lesson: lessonObject } = data;
    if (lessonObject) {
        let lessonSpec = null;
        try {
            const response = await httpAPI('', `${apiPaths.getLessonSpec}/${lesson._id}`, { method: 'GET' });
            lessonSpec = response.data.lessonSpec;
        } catch (e) {
            // ignore missing less-spec
        }
        const nodeStruct = { root: lessonObject.lessonNode.nodeID, nodeMap: lessonObject.nodeMap, lessonSpec, timestamp: Date.now() };
        await exporNodeStruct(nodeStruct);
    }
};

export const playTTS = (audioEl, text, e, options = {}) => {
    const audio = audioEl || document.getElementById('player-audio-el');
    if (audio) {
        const speaker = { male: { voice: 'nkyuwon', pitch: 1 }, female: { voice: 'nara', pitch: 0 } }[options.speaker || 'female']; // njooahn
        const speed = options.speed || 2; // default to 80% for now with CLOVA
        const pitch = options.pitch || speaker.pitch;
        const samplingRate = options.samplingRate || 24000;
        const src = `/api/nlp/clovatts?text=${text}&speed=${speed}&speaker=${speaker.voice}&pitch=${pitch}&samplingRate=${samplingRate}&app=l`;

        if (audio.readyState === 0 || audio.getAttribute('src') !== src) {
            audio.setAttribute('src', src);
        }
        // this rewind does NOT work on Safari, so need to redownload the .mp3 on every play click for that browser
        if (!browser.isSafari && !(browser.majorVersion && browser.majorVersion.match(/iPhone/i))) {
            audio.currentTime = 0;
        } else {
            audio.load();
        }
        audio.play();
    }
    if (e) e.stopPropagation();
};

export const playAzureTTS = (audioEl, text, e, options = {}) => {
    const audio = audioEl || document.getElementById('player-audio-el');
    if (audio) {
        const speaker = options.speaker || 'JiMin';
        const speed = options.speed || '100%';
        const pitch = options.pitch || 'medium';

        const src = `/api/nlp/azuretts?text=${text}&speed=${speed}&speaker=${speaker}&pitch=${pitch}&app=l`;

        if (audio.readyState === 0 || audio.getAttribute('src') !== src) {
            audio.setAttribute('src', src);
        }
        // this rewind does NOT work on Safari, so need to redownload the .mp3 on every play click for that browser
        if (!browser.isSafari && !(browser.majorVersion && browser.majorVersion.match(/iPhone/i))) {
            audio.currentTime = 0;
        } else {
            audio.load();
        }
        audio.play();
    }
    if (e) e.stopPropagation();
};








